import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {TemperatureService} from '../services/temperature.service';
import { forkJoin, Observable, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';

import {TemperatureReadHeadModel} from '../models/tempread-head-model';
import {TemperatureReadingModel} from '../models/tempread-reading-model';
import {TemperatureReadingModelSimple} from '../models/tempread-simple-model';
import {TemperatureReadingModelExtremes} from '../models/tempread-extremes-model';
import { ChartConfiguration, ChartType } from 'chart.js';
import { temperatureCrosshairPlugin } from '../temperature-crosshair.plugin';

@Component({
    selector: 'app-temperatures',
    templateUrl: './temperatures.component.html',
    styleUrls: ['./temperatures.component.css'],
    providers: [TemperatureService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TemperaturesComponent implements OnInit {
  constructor(
    private temperatureService: TemperatureService,
    private route: ActivatedRoute
    ) {}

    public lineChartData: ChartConfiguration['data'] = {
      datasets: [],
      labels: []
    };

    temperatureObject: TemperatureReadingModelSimple;
    temperatureArr: TemperatureReadHeadModel | null = null;
    temperatureExtremes: TemperatureReadingModelExtremes | null = null;

    sensorId = '6';
    isLoading = false;
    loadError = false;
    private readonly destroyRef = inject(DestroyRef);

    public lineChartOptions: any = {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        x: {
          ticks: {
            display: true,
            maxTicksLimit: 6
          }
        },
        y: {
          ticks: {
            maxTicksLimit: 5
          }
        }
      },
      elements: {
        point: {
          radius: 0
        },
        line: {
          tension: 0.4
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false
        }
      }
    };

    public lineChartColors: Array<any> = [
      { // grey
        backgroundColor: 'rgba(148,159,177,0.2)',
        borderColor: 'rgba(148,159,177,1)',
        pointBackgroundColor: 'rgba(148,159,177,1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(148,159,177,0.8)'
      },
      { // dark grey
        backgroundColor: 'rgba(77,83,96,0.2)',
        borderColor: 'rgba(77,83,96,1)',
        pointBackgroundColor: 'rgba(77,83,96,1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(77,83,96,1)'
      },
      { // grey
        backgroundColor: 'rgba(148,159,177,0.2)',
        borderColor: 'rgba(148,159,177,1)',
        pointBackgroundColor: 'rgba(148,159,177,1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(148,159,177,0.8)'
      }
    ];

    public lineChartType : ChartType = 'line';
    public chartPlugins = [temperatureCrosshairPlugin];

    ngOnInit() {
      this.route.paramMap.pipe(
        map(params => params.get('id') ?? '6'),
        distinctUntilChanged(),
        tap(sensorId => this.sensorId = sensorId),
        switchMap(sensorId => timer(0, 60 * 1000).pipe(
          tap(() => {
            this.isLoading = true;
            this.loadError = false;
          }),
          switchMap(() => this.loadData(sensorId))
        )),
        tap(() => this.isLoading = false),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(({ latest, history, extremes }) => {
        if (latest) {
          this.temperatureObject = latest;
        }
        if (history) {
          this.temperatureArr = history;
          this.fixlineChartData();
        }
        if (extremes) {
          this.temperatureExtremes = extremes;
        }
      });
    }

    private loadData(sensorId: string): Observable<{
      latest: TemperatureReadingModelSimple | null;
      history: TemperatureReadHeadModel | null;
      extremes: TemperatureReadingModelExtremes | null;
    }> {
      return forkJoin({
        latest: this.temperatureService.getLatest(sensorId).pipe(catchError(() => of(null))),
        history: this.temperatureService.getLastDay(sensorId).pipe(catchError(() => of(null))),
        extremes: this.temperatureService.getLastDayExtremes(sensorId).pipe(catchError(() => of(null)))
      }).pipe(
        tap(result => this.loadError = !result.latest && !result.history && !result.extremes)
      );
    }

          private fixlineChartData() {
            const dataArr = new Array<number>();
            const labelArr = new Array<string>();

            if (this.temperatureArr != null) {
              const tempModel: Array<TemperatureReadingModel> = this.temperatureArr.sensorReadingsLst
                .filter(reading => Number.isFinite(reading.sensorValue) && reading.sensorReadingDate != null)
                .slice()
                .sort((a, b) => new Date(a.sensorReadingDate).getTime() - new Date(b.sensorReadingDate).getTime());

              for (const element of tempModel) {
                dataArr.push(element.sensorValue);
                labelArr.push(moment(element.sensorReadingDate).format('DD/MM HH:mm'));
              }

              this.lineChartData = {
                labels: labelArr,
                datasets: [
                  {
                    data: dataArr as number[],
                    label: this.temperatureArr.sensorDescription,
                    fill: 'origin',
                    backgroundColor: 'rgba(148,159,177,0.2)',
                    borderColor: 'rgba(148,159,177,1)',
                    pointBackgroundColor: 'rgba(148,159,177,1)',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(148,159,177,0.8)'
                  }
                ]
              };
            } else {
              this.lineChartData = {
                labels: [],
                datasets: []
              };
            }
          }

          // events
          public chartClicked(e: any): void {
          }

          public chartHovered(e: any): void {
          }
        }
